// Bookmark Manager MVP - Main Application

import './style.css';
import auth from './auth.js';
import { renderLoginPage, initLoginPage } from './components/LoginPage.js';
import { loadBookmarks, loadCategories, loadTags, renderBookmarkList } from './components/BookmarkList.js';
import { renderBookmarkForm, initBookmarkForm } from './components/BookmarkForm.js';
import { renderCategoryManager, initCategoryManager } from './components/CategoryManager.js';
import { renderTagManager, initTagManager } from './components/TagManager.js';
import { renderSearchBar, initSearchBar } from './components/SearchBar.js';
import { renderImportExport, initImportExport } from './components/ImportExport.js';
import { renderAdminDashboard, initAdminDashboard } from './components/AdminDashboard.js';
import { renderAccessDenied, initAccessDenied } from './components/AccessDenied.js';

// Application state
let currentView = 'login';
let currentRoute = '/';

// Initialize app
function init() {
  // Set up auth state change listener
  auth.onAuthStateChanged = handleAuthStateChange;

  // Set up custom event listeners
  setupEventListeners();

  // Set up hash-based routing
  window.addEventListener('hashchange', handleRoute);

  // Initial render
  handleAuthStateChange();
}

// Handle hash-based routing
function handleRoute() {
  const hash = window.location.hash || '#/';
  currentRoute = hash;

  // If not authenticated, only allow login
  if (!auth.isAuthenticated()) {
    if (hash === '#/admin') {
      // Store intended destination and redirect to login
      sessionStorage.setItem('redirect_after_login', hash);
    }
    renderLogin();
    return;
  }

  // Route to appropriate view
  if (hash === '#/admin') {
    const user = auth.getUser();
    if (user?.is_admin) {
      renderAdminPage();
    } else {
      renderAccessDeniedPage();
    }
  } else {
    // Default: render main app
    renderApp();
  }
}

function handleAuthStateChange() {
  if (auth.isAuthenticated()) {
    // Check for redirect after login
    const redirect = sessionStorage.getItem('redirect_after_login');
    if (redirect) {
      sessionStorage.removeItem('redirect_after_login');
      window.location.hash = redirect;
      return;
    }
    handleRoute();
  } else {
    renderLogin();
  }
}

function renderLogin() {
  currentView = 'login';
  const app = document.getElementById('app');
  app.innerHTML = renderLoginPage();
  initLoginPage();
}

async function renderAdminPage() {
  currentView = 'admin';
  const user = auth.getUser();
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="app-container">
      <header class="app-header">
        <div class="header-content">
          <h1 class="app-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            Admin Dashboard
          </h1>

          <div class="header-actions">
            <a href="#/" class="btn btn-secondary">Zurück zur App</a>
            <span class="user-info">${escapeHtml(user?.email || 'Admin')}</span>
            <button id="logout-btn" class="btn btn-secondary">Abmelden</button>
          </div>
        </div>
      </header>

      <main class="app-main admin-main">
        <div id="admin-content" class="admin-content">
          ${renderAdminDashboard()}
        </div>
      </main>

      <div id="notification-container"></div>
    </div>
  `;

  // Initialize logout button
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    auth.logout();
  });

  // Initialize admin dashboard
  await initAdminDashboard();
}

function renderAccessDeniedPage() {
  currentView = 'access-denied';
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="app-container">
      <header class="app-header">
        <div class="header-content">
          <h1 class="app-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
            </svg>
            Bookmark Manager
          </h1>
        </div>
      </header>

      <main class="app-main">
        <div class="access-denied-container">
          ${renderAccessDenied()}
        </div>
      </main>
    </div>
  `;

  initAccessDenied();
}

async function renderApp() {
  currentView = 'app';
  const user = auth.getUser();
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="app-container">
      <header class="app-header">
        <div class="header-content">
          <h1 class="app-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
            </svg>
            Bookmark Manager
          </h1>

          <div class="header-actions">
            <span class="user-info">${escapeHtml(user?.email || 'Benutzer')}</span>
            <button id="logout-btn" class="btn btn-secondary">Abmelden</button>
          </div>
        </div>
      </header>

      <main class="app-main">
        <aside class="app-sidebar">
          <div class="sidebar-section">
            <button id="add-bookmark-btn" class="btn btn-primary btn-block">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Neues Lesezeichen
            </button>
          </div>

          <div class="sidebar-section">
            <h3>Verwaltung</h3>
            <button id="manage-categories-btn" class="btn btn-sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
              Kategorien
            </button>

            <button id="manage-tags-btn" class="btn btn-sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              Tags
            </button>

            <button id="import-export-btn" class="btn btn-sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Import/Export
            </button>
          </div>

          ${user?.is_admin ? `
          <div class="sidebar-section">
            <h3>Administration</h3>
            <a href="#/admin" class="btn btn-sidebar btn-admin">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
              Admin Dashboard
            </a>
          </div>
          ` : ''}
        </aside>

        <div class="app-content">
          ${renderSearchBar()}

          <div id="bookmark-list" class="bookmark-list">
            <p class="loading">Lade Lesezeichen...</p>
          </div>
        </div>
      </main>

      <div id="notification-container"></div>
    </div>
  `;

  // Initialize components
  initApp();

  // Load data
  await Promise.all([
    loadBookmarks(),
    loadCategories(),
    loadTags(),
  ]);

  initSearchBar();
}

function initApp() {
  // Logout button
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    auth.logout();
  });

  // Add bookmark button
  document.getElementById('add-bookmark-btn')?.addEventListener('click', () => {
    showBookmarkForm();
  });

  // Manage categories button
  document.getElementById('manage-categories-btn')?.addEventListener('click', () => {
    showCategoryManager();
  });

  // Manage tags button
  document.getElementById('manage-tags-btn')?.addEventListener('click', () => {
    showTagManager();
  });

  // Import/Export button
  document.getElementById('import-export-btn')?.addEventListener('click', () => {
    showImportExport();
  });
}

function setupEventListeners() {
  // Bookmark form event
  document.addEventListener('show-bookmark-form', (e) => {
    showBookmarkForm(e.detail);
  });

  // Tag form event
  document.addEventListener('show-tag-form', () => {
    showTagManager();
  });

  // Notification event
  document.addEventListener('show-notification', (e) => {
    showNotification(e.detail.type, e.detail.message);
  });
}

function showBookmarkForm(bookmark = null) {
  const existingModal = document.getElementById('bookmark-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modalHTML = renderBookmarkForm(bookmark);
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  initBookmarkForm();
}

function showCategoryManager() {
  const existingModal = document.getElementById('category-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modalHTML = renderCategoryManager();
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  initCategoryManager();
}

function showTagManager() {
  const existingModal = document.getElementById('tag-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modalHTML = renderTagManager();
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  initTagManager();
}

function showImportExport() {
  const existingModal = document.getElementById('import-export-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modalHTML = renderImportExport();
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  initImportExport();
}

function showNotification(type, message) {
  const container = document.getElementById('notification-container');
  if (!container) return;

  const notification = document.createElement('div');
  notification.className = `notification notification-${type}`;
  notification.textContent = message;

  container.appendChild(notification);

  // Auto-remove after 3 seconds
  setTimeout(() => {
    notification.classList.add('fade-out');
    setTimeout(() => {
      notification.remove();
    }, 300);
  }, 3000);
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Start the app
init();
